import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer, Input, Button, List, Typography, Space, Spin, Avatar, Tag, message
} from 'antd';
import {
  SendOutlined, RobotOutlined, UserOutlined, BulbOutlined, ReloadOutlined
} from '@ant-design/icons';
import { chatService } from '../services/api';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const ChatPanel = ({ visible, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const messagesEndRef = useRef(null);

  // Load suggestions on mount
  useEffect(() => {
    const loadSuggestions = async () => {
      setLoadingSuggestions(true);
      try {
        const data = await chatService.getSuggestions();
        setSuggestions(data.suggestions || []);
      } catch (error) {
        console.error('Failed to load suggestions:', error);
      } finally {
        setLoadingSuggestions(false);
      }
    };
    loadSuggestions();
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (text = inputValue) => {
    if (!text.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      const response = await chatService.query(text);
      const aiMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        dataReferenced: response.data_referenced,
        suggestedActions: response.suggested_actions,
        error: response.error
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      message.error('Failed to get response from AI assistant');
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    setMessages([]);
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderMessage = (msg, idx) => {
    const isUser = msg.role === 'user';

    return (
      <div
        key={idx}
        style={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          marginBottom: 16,
          padding: '0 8px'
        }}
      >
        <Space align="start" style={{ maxWidth: '85%' }}>
          {!isUser && (
            <Avatar
              icon={<RobotOutlined />}
              style={{ backgroundColor: msg.error ? '#ff4d4f' : '#1890ff', flexShrink: 0 }}
            />
          )}
          <div>
            <div
              style={{
                background: isUser ? '#1890ff' : msg.error ? '#fff1f0' : '#f5f5f5',
                color: isUser ? '#fff' : '#000',
                padding: '10px 14px',
                borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              <Text style={{ color: 'inherit' }}>{msg.content}</Text>
            </div>

            {/* Data referenced tags */}
            {msg.dataReferenced && msg.dataReferenced.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {msg.dataReferenced.map((ref, i) => (
                  <Tag key={i} color="blue" style={{ marginBottom: 2, fontSize: 11 }}>
                    {ref.replace('_', ' ')}
                  </Tag>
                ))}
              </div>
            )}

            {/* Timestamp */}
            <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
              {formatTime(msg.timestamp)}
            </Text>
          </div>
          {isUser && (
            <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#87d068', flexShrink: 0 }} />
          )}
        </Space>
      </div>
    );
  };

  const renderWelcome = () => (
    <div style={{ padding: 24, textAlign: 'center' }}>
      <Avatar
        size={64}
        icon={<RobotOutlined />}
        style={{ backgroundColor: '#1890ff', marginBottom: 16 }}
      />
      <Paragraph style={{ fontSize: 16, marginBottom: 8 }}>
        Hi! I'm your AI assistant.
      </Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        I can help you analyze your vendor data, spending patterns, and identify cost savings. Try asking:
      </Paragraph>

      <Spin spinning={loadingSuggestions}>
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {suggestions.slice(0, 5).map((suggestion, idx) => (
            <Button
              key={idx}
              type="dashed"
              block
              icon={<BulbOutlined />}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{ textAlign: 'left', height: 'auto', padding: '8px 12px', whiteSpace: 'normal' }}
            >
              {suggestion}
            </Button>
          ))}
        </Space>
      </Spin>
    </div>
  );

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <RobotOutlined style={{ fontSize: 18 }} />
            <span>AI Assistant</span>
          </Space>
          {messages.length > 0 && (
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleClearChat}
            >
              Clear
            </Button>
          )}
        </div>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={450}
      styles={{
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100% - 55px)'
        }
      }}
    >
      {/* Messages Area */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '16px 8px',
        background: '#fafafa'
      }}>
        {messages.length === 0 ? (
          renderWelcome()
        ) : (
          <>
            {messages.map((msg, idx) => renderMessage(msg, idx))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 16, padding: '0 8px' }}>
                <Space align="start">
                  <Avatar icon={<RobotOutlined />} style={{ backgroundColor: '#1890ff' }} />
                  <div
                    style={{
                      background: '#f5f5f5',
                      padding: '10px 14px',
                      borderRadius: '16px 16px 16px 4px'
                    }}
                  >
                    <Spin size="small" />
                    <Text type="secondary" style={{ marginLeft: 8 }}>Thinking...</Text>
                  </div>
                </Space>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={{
        padding: 16,
        borderTop: '1px solid #f0f0f0',
        background: '#fff'
      }}>
        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your vendor data..."
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={loading}
            style={{ resize: 'none' }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => handleSend()}
            disabled={!inputValue.trim() || loading}
            style={{ height: 'auto' }}
          />
        </Space.Compact>
        <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
          Press Enter to send, Shift+Enter for new line
        </Text>
      </div>
    </Drawer>
  );
};

export default ChatPanel;
